'use client';
import { useState } from 'react';
import { FormikHelpers, FormikErrors } from 'formik';
import { useRouter, useSearchParams } from 'next/navigation';

import Form, { BaseFormProps } from '@/components/form/lib/Form/Form';
import Text from '@/components/common/Text/Text';
import InputField from '@/components/form/lib/InputField/InputField';
import SubmitButton from '@/components/form/lib/SubmitButton/SubmitButton';
import { isValidEmail } from '@/util/regexp';

import './ForgotPasswordForm.style.scss';
import { CACHE_TIME } from '@/const/cache';

export type ForgotPasswordFormValues = {
  email: string;
};

interface ForgotPasswordFormProps
  extends Omit<
    BaseFormProps<ForgotPasswordFormValues>,
    'children' | 'onSubmit'
  > {
  onSubmit?: (
    values: ForgotPasswordFormValues,
    actions: FormikHelpers<ForgotPasswordFormValues>
  ) => void;
}

function defaultValidation(
  values: ForgotPasswordFormValues
): FormikErrors<ForgotPasswordFormValues> {
  const { email } = values;
  const errors: FormikErrors<ForgotPasswordFormValues> = {};

  if (!isValidEmail(email)) {
    errors.email = 'This value cannot be blank';
  }

  return errors;
}

export default function ForgotPasswordForm(props: ForgotPasswordFormProps) {
  const {
    initialValues,
    validate = defaultValidation,
    onSubmit,
    validateOnBlur = true,
    validateOnChange = false
  } = props;
  const urlParams = useSearchParams();
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false);
  const isFormSent = urlParams.has('sent');

  const handleSubmit = async (values: ForgotPasswordFormValues) => {
    setLoading(true);
    try {
      await fetch('/api/reset-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(values),
        next: {
          revalidate: CACHE_TIME.NO_CACHE
        }
      });
      // No need to reset loading state. Submission ALWAYS
      // pushes the user to the forgot screen which does not render
      // anything regarding loading state
    } catch {
      console.error(`[ForgotPasswordForm] Submission error`);
    } finally {
      router.replace('/login/forgot?sent');
    }
  };

  return (
    <Form<ForgotPasswordFormValues>
      className='ForgotPasswordForm'
      initialValues={initialValues}
      validate={validate}
      onSubmit={onSubmit || handleSubmit}
      validateOnBlur={validateOnBlur}
      validateOnChange={validateOnChange}
    >
      <Text
        className='ForgotPasswordForm-title ForgotPasswordForm-Text'
        variant='title-xl'
        italic
      >
        Forgot Password
      </Text>
      {isFormSent ? (
        <Text className='ForgotPasswordForm-Text ForgotPasswordForm-sent'>
          Your reset password email has been sent. Please click the link in the
          email to reset your password.
        </Text>
      ) : (
        <>
          <Text className='ForgotPasswordForm-instructions ForgotPasswordForm-Text'>
            Enter your email address and we&apos;ll send you an email with a
            link to reset your password
          </Text>
          <InputField
            name='email'
            label='Email'
          />
          <SubmitButton
            disabled={loading}
            isLoading={loading}
          >
            Submit
          </SubmitButton>
        </>
      )}
    </Form>
  );
}
